import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  MessageCircle, AlertTriangle, Send, Camera, Mic,
  HeartCrack, Wrench, Pill, Check, Phone, X, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { chatPaziente } from '@/data/mockPatientData';

const tabs = [
  { id: 'chat', label: 'Messaggi', icon: MessageCircle },
  { id: 'sos', label: 'Richiedi Aiuto', icon: AlertTriangle },
];

/* ══════ PROBLEMI SOS ══════ */
const problemiSOS = [
  { id: 'sto-male', label: 'Sto male', desc: 'Dolore, nausea, febbre o malessere', icon: HeartCrack, color: '#EF4444' },
  { id: 'dispositivo', label: 'Problema col dispositivo', desc: 'Strumento guasto o malfunzionante', icon: Wrench, color: '#F59E0B' },
  { id: 'farmaci', label: 'Ho finito i farmaci', desc: 'Necessito nuova prescrizione', icon: Pill, color: '#8B5CF6' },
];

/* ══════ TAB SELECTOR ══════ */
function TabSelector({ active, onChange }) {
  return (
    <div className="flex gap-2 p-1.5 bg-muted/30 rounded-3xl border border-border/20 w-full sm:w-fit" data-testid="tab-selector">
      {tabs.map((t) => {
        const Icon = t.icon;
        const isActive = active === t.id;
        return (
          <button
            key={t.id}
            onClick={() => onChange(t.id)}
            className={`flex-1 sm:flex-initial flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-2xl text-base font-bold transition-all ${isActive ? (t.id === 'sos' ? 'bg-red-50 text-red-700 shadow-md' : 'bg-card text-foreground shadow-md') : 'text-muted-foreground hover:text-foreground'}`}
            data-testid={`tab-${t.id}`}
          >
            <Icon className="w-5 h-5" />
            {t.label}
            {t.id === 'sos' && <Badge className="rounded-full h-5 px-1.5 bg-red-500 text-white text-[9px] border-0 ml-0.5">SOS</Badge>}
          </button>
        );
      })}
    </div>
  );
}

/* ══════ AREA A: CHAT ══════ */
function ChatArea() {
  const [messages, setMessages] = useState(chatPaziente);
  const [input, setInput] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const handleSend = () => {
    if (!input.trim()) return;
    setMessages(prev => [...prev, {
      id: `cp-new-${Date.now()}`,
      autore: 'Elena',
      ruolo: 'Paziente',
      testo: input.trim(),
      timestamp: new Date().toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', ''),
      mio: true,
    }]);
    setInput('');
    toast.success('Messaggio inviato');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  return (
    <div className="space-y-4" data-testid="chat-area">
      {/* Chat Header */}
      <Card className="border-0 shadow-[0_1px_4px_rgba(0,0,0,0.05)] rounded-3xl">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-sky-400 to-teal-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
            MC
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-foreground">Infermiere Marco e Centrale Operativa</p>
            <p className="text-[11px] text-muted-foreground">Scrivi qui per qualsiasi domanda sul tuo percorso di cura</p>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[9px] font-bold text-emerald-700">Online</span>
          </div>
        </CardContent>
      </Card>

      {/* Chat Messages */}
      <Card className="border-0 shadow-[0_1px_4px_rgba(0,0,0,0.05)] rounded-3xl overflow-hidden">
        <div className="h-[380px] flex flex-col">
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" ref={scrollRef} data-testid="chat-messages">
            {messages.map((m, i) => {
              const showHeader = i === 0 || messages[i - 1]?.autore !== m.autore;
              return (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className={`flex ${m.mio ? 'justify-end' : 'justify-start'}`}
                  data-testid={`msg-${m.id}`}
                >
                  <div className={`max-w-[80%] ${m.mio ? 'items-end' : 'items-start'}`}>
                    {showHeader && !m.mio && (
                      <p className="text-[11px] font-bold text-muted-foreground mb-1 ml-1">{m.autore}</p>
                    )}
                    <div className={`px-4 py-3 text-[15px] leading-relaxed ${m.mio ? 'bg-gradient-to-r from-sky-500 to-teal-500 text-white rounded-3xl rounded-tr-lg' : 'bg-muted/30 text-foreground rounded-3xl rounded-tl-lg'}`}>
                      {m.testo}
                    </div>
                    <p className={`text-[9px] text-muted-foreground mt-1 ${m.mio ? 'text-right mr-1' : 'ml-1'}`}>{m.timestamp}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Input */}
          <div className="px-4 py-3 border-t border-border/15 flex items-end gap-2" data-testid="chat-input-bar">
            <button
              className="w-11 h-11 rounded-2xl flex items-center justify-center text-muted-foreground hover:bg-muted/30 transition-colors flex-shrink-0"
              onClick={() => toast('Funzionalita foto in arrivo')}
              data-testid="btn-camera"
            >
              <Camera className="w-5 h-5" />
            </button>
            <button
              className="w-11 h-11 rounded-2xl flex items-center justify-center text-muted-foreground hover:bg-muted/30 transition-colors flex-shrink-0"
              onClick={() => toast('Funzionalita vocale in arrivo')}
              data-testid="btn-vocal"
            >
              <Mic className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <textarea
                className="w-full min-h-[44px] max-h-28 rounded-2xl border border-border/40 bg-card px-4 py-2.5 text-[15px] resize-none focus:outline-none focus:ring-2 focus:ring-sky-200"
                placeholder="Scrivi un messaggio..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
                data-testid="chat-input"
              />
            </div>
            <button
              onClick={handleSend}
              disabled={!input.trim()}
              className="w-11 h-11 rounded-2xl flex items-center justify-center bg-gradient-to-r from-sky-500 to-teal-500 text-white hover:from-sky-600 hover:to-teal-600 transition-all flex-shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="btn-send"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
}

/* ══════ AREA B: SOS ══════ */
function SOSArea() {
  const [step, setStep] = useState(0); // 0: idle, 1: selezione, 2: foto, 3: conferma
  const [selectedProblema, setSelectedProblema] = useState(null);

  const handleSubmit = () => {
    setStep(3);
    toast.success('Segnalazione inviata alla Centrale Operativa', {
      description: 'Un operatore ti contattara il prima possibile.',
    });
  };

  const handleReset = () => {
    setStep(0);
    setSelectedProblema(null);
  };

  if (step === 3) {
    return (
      <div className="space-y-5" data-testid="sos-conferma">
        <Card className="border-0 shadow-[0_2px_12px_rgba(0,0,0,0.06)] rounded-3xl">
          <CardContent className="p-10 flex flex-col items-center text-center">
            <motion.div
              initial={{ scale: 0 }} animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200 }}
              className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mb-5"
            >
              <Check className="w-8 h-8 text-emerald-500" />
            </motion.div>
            <h2 className="text-xl font-bold text-foreground">Segnalazione Inviata</h2>
            <p className="text-base text-muted-foreground mt-3 leading-relaxed max-w-sm">
              La Centrale Operativa ha ricevuto la tua segnalazione. Un operatore ti contattara al piu presto.
            </p>
            <div className="mt-5 px-5 py-3 rounded-2xl bg-sky-50 border border-sky-100">
              <p className="text-sm font-bold text-sky-700 flex items-center gap-2">
                <Phone className="w-4 h-4" />In caso di emergenza: 118
              </p>
            </div>
            <Button className="mt-6 h-12 px-8 rounded-2xl text-base" onClick={handleReset} data-testid="btn-nuova-segnalazione">
              Torna Indietro
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="sos-area">
      {/* Banner SOS */}
      {step === 0 && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <button
            onClick={() => setStep(1)}
            className="w-full p-6 rounded-3xl bg-gradient-to-br from-orange-400 to-red-500 text-white shadow-lg hover:shadow-xl hover:scale-[1.01] transition-all active:scale-[0.99]"
            data-testid="btn-sos-main"
          >
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-sm flex-shrink-0">
                <AlertTriangle className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-xl font-bold">Segnala un Problema</p>
                <p className="text-base text-white/80 mt-1">Richiedi aiuto alla Centrale Operativa</p>
              </div>
              <ChevronRight className="w-6 h-6 text-white/50 flex-shrink-0" />
            </div>
          </button>
        </motion.div>
      )}

      {/* Step 1: Cosa succede? */}
      {step >= 1 && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <div className="flex items-center justify-between px-1">
            <p className="text-base font-bold text-foreground">Cosa succede?</p>
            <button onClick={handleReset} className="text-sm text-muted-foreground hover:text-foreground" data-testid="btn-sos-annulla">Annulla</button>
          </div>
          <div className="space-y-3">
            {problemiSOS.map((p, i) => {
              const Icon = p.icon;
              const isSelected = selectedProblema === p.id;
              return (
                <motion.div key={p.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}>
                  <button
                    onClick={() => { setSelectedProblema(p.id); setStep(2); }}
                    className={`w-full flex items-center gap-4 p-5 rounded-3xl border-2 transition-all text-left ${isSelected ? 'border-red-300 bg-red-50/50 shadow-sm' : 'border-border/20 bg-card hover:border-border/40 shadow-[0_1px_4px_rgba(0,0,0,0.04)]'}`}
                    data-testid={`btn-problema-${p.id}`}
                  >
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${p.color}12` }}>
                      <Icon className="w-6 h-6" style={{ color: p.color }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-base font-bold text-foreground">{p.label}</p>
                      <p className="text-sm text-muted-foreground">{p.desc}</p>
                    </div>
                    {isSelected && <Check className="w-5 h-5 text-red-500 flex-shrink-0" />}
                  </button>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Step 2: Foto + Invio */}
      {step === 2 && selectedProblema && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <p className="text-base font-bold text-foreground px-1">Vuoi allegare una foto?</p>
          <Card className="border-0 shadow-[0_1px_4px_rgba(0,0,0,0.05)] rounded-3xl">
            <CardContent className="p-5">
              <button
                className="w-full flex items-center justify-center gap-3 h-20 rounded-2xl border-2 border-dashed border-border/40 hover:border-sky-300 hover:bg-sky-50/30 transition-all"
                onClick={() => toast('Funzionalita fotocamera in arrivo')}
                data-testid="btn-allega-foto"
              >
                <Camera className="w-7 h-7 text-muted-foreground" />
                <span className="text-base font-bold text-muted-foreground">Scatta o Allega Foto</span>
              </button>
              <p className="text-[11px] text-muted-foreground text-center mt-2">Opzionale — Puoi inviare anche senza foto</p>
            </CardContent>
          </Card>

          <Button
            className="w-full h-14 rounded-3xl text-lg font-bold gap-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-lg"
            onClick={handleSubmit}
            data-testid="btn-invia-segnalazione"
          >
            <AlertTriangle className="w-5 h-5" />Invia Segnalazione alla Centrale
          </Button>
        </motion.div>
      )}

      {/* Info box */}
      <Card className="border-0 shadow-[0_1px_4px_rgba(0,0,0,0.04)] rounded-3xl">
        <CardContent className="p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-2xl bg-red-50 flex items-center justify-center flex-shrink-0">
            <Phone className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <p className="text-sm font-bold text-foreground">Emergenza sanitaria?</p>
            <p className="text-[11px] text-muted-foreground">Per emergenze gravi chiama subito il <strong>118</strong></p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ══════ PAGINA PRINCIPALE ══════ */
export default function PatientCommunications() {
  const [activeTab, setActiveTab] = useState('chat');

  return (
    <AppLayout role="paziente">
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="patient-communications">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-violet-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground" data-testid="page-title">Comunicazioni</h1>
            <p className="text-sm text-muted-foreground">Messaggi, segnalazioni e richieste di aiuto</p>
          </div>
        </div>

        {/* Tabs */}
        <TabSelector active={activeTab} onChange={setActiveTab} />

        {/* Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'chat' ? (
            <motion.div key="chat" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <ChatArea />
            </motion.div>
          ) : (
            <motion.div key="sos" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <SOSArea />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AppLayout>
  );
}
